import { Icon } from '@iconify/react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { FormFieldWrapper } from '../../shared/components';
import { MasterConfig } from './masters.config';
import MasterFormDialog, { MasterRow } from './MasterFormDialog';
import { FilterOption } from './workJobReport.api';

interface MasterSelectFieldProps {
  label: string;
  value: number | null;
  options: FilterOption[];
  onChange: (id: number | null) => void;
  /** Omit to render a plain picker with no add/edit affordances (Status). */
  master?: MasterConfig;
  required?: boolean;
  /**
   * Handed every row added or edited here. The parent owns them: it has to merge a
   * brand-new row into the list it reads the save payload from, or a locality added and
   * then saved in the same breath resolves to no label and is dropped on the way out.
   */
  onMasterSaved?: (row: MasterRow) => void;
  /**
   * `floating` (default) is MUI's in-box label, as the edit dialog uses. `above` puts the
   * label over the box through FormFieldWrapper, matching the New Job Creation fields.
   */
  labelPlacement?: 'floating' | 'above';
  /** Validation message shown under the box; only rendered with `labelPlacement='above'`. */
  errorMessage?: string;
}

/**
 * Type-to-search picker over one master list, with "+ Add" beside it and a pencil on each
 * row of the list.
 *
 * A row saved here is reported to the parent, which merges it into the options it passes
 * back down — so the new value is selectable, and readable by the save payload, without
 * waiting for the page to refetch.
 */
export default function MasterSelectField({
  label,
  value,
  options,
  onChange,
  master,
  required = false,
  onMasterSaved,
  labelPlacement = 'floating',
  errorMessage,
}: MasterSelectFieldProps) {
  const labelAbove = labelPlacement === 'above';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [typed, setTyped] = useState('');

  const selected = options.find((option) => option.id === value) ?? null;

  const openAdd = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (id: number) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  const handleSaved = (row: MasterRow) => {
    onMasterSaved?.(row);
    // A new row is what the user was reaching for; an edit must not hijack the selection.
    if (editingId == null) onChange(row.id);
    toast.success(`${master?.entityName} ${editingId == null ? 'added' : 'updated'}`);
  };

  const picker = (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
        <Autocomplete
          sx={{ flex: 1, minWidth: 0 }}
          options={options}
          value={selected}
          onChange={(_event, option) => onChange(option ? option.id : null)}
          onInputChange={(_event, next) => setTyped(next)}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, picked) => option.id === picked.id}
          size='small'
          // Type-to-search: the list opens on focus and Enter takes the top match, so a
          // known client is a few keystrokes rather than a scroll through sixty owners.
          openOnFocus
          autoHighlight
          renderOption={(props, option) => {
            const { key, ...liProps } = props as typeof props & { key: string };
            return (
              <Box
                component='li'
                key={key}
                {...liProps}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {/*
                  The name takes the leftover width and truncates; the pencil keeps its
                  place at the right edge. Without `minWidth: 0` a long client name refuses
                  to shrink below its text and pushes the icon onto a second line.
                */}
                <Box
                  component='span'
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={option.label}
                >
                  {option.label}
                </Box>
                {master && (
                  <Tooltip title={`Edit ${master.entityName.toLowerCase()}`}>
                    <IconButton
                      size='small'
                      aria-label={`Edit ${option.label}`}
                      sx={{ flexShrink: 0 }}
                      // The row's own click would select the option and close the list.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(option.id);
                      }}
                    >
                      <Icon icon='mdi:pencil-outline' width={15} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          }}
          // An empty list means the master data never arrived, not that the master table
          // is empty — say so, because a bare "No options" reads as a broken search box.
          noOptionsText={options.length === 0 ? 'List not loaded — reload the page' : 'No match'}
          renderInput={(params) => (
            <TextField
              {...params}
              label={labelAbove ? undefined : label}
              required={labelAbove ? undefined : required}
              error={Boolean(errorMessage)}
              placeholder='Search…'
            />
          )}
        />

        {master && (
          <Tooltip title={`Add ${master.entityName.toLowerCase()}`}>
            <IconButton
              size='small'
              onClick={openAdd}
              aria-label={`Add ${master.entityName.toLowerCase()}`}
              sx={{ marginTop: '2px', flexShrink: 0 }}
            >
              <Icon icon='mdi:plus-box-outline' width={20} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
  );

  return (
    <>
      {labelAbove ? (
        <FormFieldWrapper
          label={required ? `${label}*` : label}
          error={Boolean(errorMessage)}
          errorMessage={errorMessage}
        >
          {picker}
        </FormFieldWrapper>
      ) : (
        picker
      )}

      {master && (
        <MasterFormDialog
          open={dialogOpen}
          master={master}
          editingId={editingId}
          // Whatever was typed and matched nothing is the name they were about to add.
          initialLabel={editingId == null && !selected ? typed : ''}
          onClose={() => setDialogOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
